import { Circle, CircleCheck } from 'lucide-react';
import { Button, formatMoney, formatNumber, isSameAmount } from '@platform/design-system';
import type { ReactNode } from 'react';
import { usePageContext } from '../usePageContext.ts';
import { NO_SHIPPING_FEE, type OrderPreview, type OrderReadiness } from './new-order-form.ts';
import styles from './NewOrder.module.css';

export type OrderSummaryProps = {
  preview: OrderPreview;
  units: number;
  shippingFee: string;
  currency: string;
  readiness: OrderReadiness;
  submitLabel: string;
  submitting: boolean;
  errorMessage: string | null;
  secondaryAction?: ReactNode;
};

const READINESS_STEPS = [
  ['items', 'newOrder.readyItems'],
  ['customer', 'newOrder.readyCustomer'],
  ['delivery', 'newOrder.readyDelivery'],
] as const;

const Readiness = ({ readiness }: { readiness: OrderReadiness }) => {
  const { t } = usePageContext();
  return (
    <section className={styles['readiness']} aria-labelledby="order-readiness">
      <h3 id="order-readiness" className={styles['readinessTitle']}>
        {t('newOrder.readinessTitle')}
      </h3>
      <ul className={styles['checklist']}>
        {READINESS_STEPS.map(([step, label]) => (
          <li key={step} className={styles['check']} data-done={readiness[step]}>
            {readiness[step] ? (
              <CircleCheck size={18} aria-hidden="true" />
            ) : (
              <Circle size={18} aria-hidden="true" />
            )}
            {t(label)}
            <span className="visually-hidden">
              {readiness[step] ? t('newOrder.stepDone') : t('newOrder.stepTodo')}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export const OrderSummary = ({
  preview,
  units,
  shippingFee,
  currency,
  readiness,
  submitLabel,
  submitting,
  errorMessage,
  secondaryAction,
}: OrderSummaryProps) => {
  const { t, locale } = usePageContext();
  const money = (amount: string) => formatMoney({ amount, currency }, locale);
  return (
    <aside className={styles['summary']} aria-labelledby="order-summary">
      <h2 id="order-summary">{t('newOrder.summarySection')}</h2>
      <div className={styles['hero']}>
        <span className={styles['heroLabel']}>{t('orders.codAmount')}</span>
        <strong className={styles['heroAmount']}>{money(preview.total)}</strong>
      </div>
      <dl className={styles['totals']}>
        <div className={styles['total']}>
          <dt>{t('newOrder.unitsCount')}</dt>
          <dd className={styles['amount']}>{formatNumber(units, locale)}</dd>
        </div>
        <div className={styles['total']}>
          <dt>{t('orders.itemsSubtotal')}</dt>
          <dd className={styles['amount']}>{money(preview.itemsSubtotal)}</dd>
        </div>
        {isSameAmount(shippingFee, NO_SHIPPING_FEE) ? null : (
          <div className={styles['total']}>
            <dt>{t('orders.shippingFee')}</dt>
            <dd className={styles['amount']}>{money(shippingFee)}</dd>
          </div>
        )}
      </dl>
      <Readiness readiness={readiness} />
      <p className={styles['muted']}>{t('newOrder.feeAtDispatch')}</p>
      {errorMessage === null ? null : (
        <p className={styles['error']} role="alert">
          {errorMessage}
        </p>
      )}
      <div className={styles['submit']}>
        {secondaryAction}
        <Button type="submit" disabled={submitting}>
          {submitLabel}
        </Button>
      </div>
    </aside>
  );
};
